import React, { useState, useEffect } from 'react';
import { TrendingUp, Fuel, Wrench, Settings, ChevronDown, ChevronLeft, ChevronRight, X, Activity, Package, BarChart3 } from 'lucide-react';

const menuData = [
  {
    icon: '/assests/dh_symbol.png',
    label: 'Dashboard',
    children: [
      { label: 'Productivity', icon: TrendingUp },
      { label: 'Fuel', icon: Fuel },
      { label: 'Spares', icon: Package },
      { label: 'Maintenance', icon: Wrench },
      { label: 'Predictive Maintenance', icon: Activity }
    ]
  }
];

const Sidebar = ({ activeTab, setActiveTab, isMobileOpen = false, onMobileClose }) => {
  const [dashboardOpen, setDashboardOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Auto-collapse sidebar when screen width is 1150px or below
  useEffect(() => {
    const checkWidth = () => {
      if (window.innerWidth <= 1150) {
        setIsCollapsed(true);
      }
    };

    // Check on mount
    checkWidth();

    // Add resize listener
    window.addEventListener('resize', checkWidth);

    // Cleanup
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Handle menu item click on mobile - close sidebar after selection
  const handleMenuItemClick = (label) => {
    setActiveTab(label);
    if (onMobileClose) {
      onMobileClose();
    }
  };

  // Sidebar content component to avoid duplication
  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo + Client name */}
      <div className={`flex items-center h-[88px] ${isCollapsed && !isMobile ? 'justify-center' : 'gap-6 pl-8'} ${isCollapsed && !isMobile ? 'border-none' : 'border-b border-gray-100'} relative`}>
        {(!isCollapsed || isMobile) && (
          <>
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center overflow-hidden">
              <img
                src="/assests/mosy_logo.png"
                alt="MOSY Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col mt-2 leading-tight">
              <span className="text-xl font-bold font-inter text-gray-800 tracking-wide">MOSY</span>
            </div>
          </>
        )}
        {isCollapsed && !isMobile && (
          <div className="flex items-center justify-center mx-auto">
            <img
              src="/assests/mosy_logo.png"
              alt="MOSY Logo"
              className="w-10 h-10 object-cover rounded-full"
            />
          </div>
        )}
        {/* Close button for mobile */}
        {isMobile && (
          <button
            onClick={onMobileClose}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        )}
        {/* Collapse button for desktop */}
        {!isMobile && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center transition-colors shadow-md z-10 ${isCollapsed ? 'bg-white text-blue-700 hover:bg-gray-100' : 'bg-blue-700 text-white hover:bg-blue-800'
              }`}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        )}
      </div>
      {/* Menu */}
      <div className="flex-1 mt-4 px-4">
        {menuData.map((item, idx) => (
          <div key={item.label}>
            <button
              className={`${isCollapsed && !isMobile ? 'w-12 h-12 justify-center bg-transparent p-2 hover:bg-white/10' : 'w-[220px] justify-between bg-neutral-100 px-6 py-2'} flex items-center rounded-lg focus:outline-none group transition-all`}
              title={isCollapsed && !isMobile ? item.label : ''}
              onClick={() => {
                if (isCollapsed && !isMobile) {
                  setIsCollapsed(false);
                  setDashboardOpen(true);
                } else {
                  setDashboardOpen((open) => !open);
                }
              }}
            >
              <div className="flex items-center gap-5">
                {typeof item.icon === 'string' ? (
                  <img
                    src={item.icon}
                    alt={item.label}
                    className={`w-3 h-4 ${isCollapsed && !isMobile ? 'brightness-0 invert' : ''}`}
                  />
                ) : (
                  <item.icon className={`w-6 h-6 ${isCollapsed && !isMobile ? 'text-white' : 'text-blue-600'}`} />
                )}
                {(!isCollapsed || isMobile) && <span className="font-medium font-style-open-sans text-gray-900 text-[14px]">{item.label}</span>}
              </div>
              {(!isCollapsed || isMobile) && <ChevronDown className={`w-4 h-4 text-gray-400 group-hover:text-gray-500 transition-transform ${dashboardOpen ? 'rotate-180' : ''}`} />}
            </button>
            {dashboardOpen && (!isCollapsed || isMobile) && (
              <div className="ml-7 border-l border-gray-200 pl-5 mt-3">
                {item.children.map((sitem) => (
                  <button
                    className={`flex items-center gap-3 w-[170px] text-left px-2 py-2 mb-2 rounded transition font-style-open-sans font-normal text-[12px] ${activeTab === sitem.label
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    key={sitem.label}
                    onClick={() => handleMenuItemClick(sitem.label)}
                  >
                    {sitem.icon && <sitem.icon className={`w-4 h-4 ${activeTab === sitem.label ? 'text-blue-700' : 'text-gray-500'}`} />}
                    <span>{sitem.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onMobileClose}
          />
          {/* Sidebar Content */}
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl flex flex-col animate-slide-in">
            <SidebarContent isMobile={true} />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className={`hidden md:flex ${isCollapsed ? 'w-20 bg-[#1e5a8e] rounded-tr-2xl rounded-br-2xl' : 'w-64 bg-white'} border-r border-gray-200 h-screen flex-col transition-all duration-300`}>
        <SidebarContent isMobile={false} />
      </div>
    </>
  );
};

export default Sidebar;

